'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'

import { selectIsSmilesSession } from '@/src/redux/slices/smilesSlice'
import { useAppSelector } from '@/src/redux/store'

import {
    MEDIA_FLOATER_DEFAULT_IMAGE_MS,
    MEDIA_FLOATER_DEFAULT_VIDEO_MS
} from './constants'
import { MediaFloater } from './mediaFloater'
import { MediaFloaterModal } from './mediaFloaterModal'
import type { MediaFloaterProps, MediaItem } from './types'


const SESSION_STORAGE_KEY = 'mediaFloaterShown'

/**
 * MediaFloaterWrapper — logic layer: visibility, carousel index, video duration for progress, session storage.
 */
const MediaFloaterWrapper: React.FC<MediaFloaterProps> = ({ mediaItems, exploreOffersUrl }) => {
    const pathname = usePathname()
    const isSmilesSession = useAppSelector(selectIsSmilesSession)
    const [isVisible, setIsVisible] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)
    const [videoDurationMs, setVideoDurationMs] = useState<number | null>(null)
    const [replayNonce, setReplayNonce] = useState(0)

    const isHomepage = pathname === '/' || /^\/[a-z]{2}$/.test(pathname)

    useEffect(() => {
        if (typeof window === 'undefined') return

        const wasShown = sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true'

        if (isHomepage && !wasShown && mediaItems.length > 0 && !isSmilesSession) {
            setIsVisible(true)
        }
    }, [isHomepage, mediaItems.length, isSmilesSession])

    const handleClose = useCallback(() => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem(SESSION_STORAGE_KEY, 'true')
        }
        setIsVisible(false)
        setIsModalOpen(false)
    }, [])

    const handleCloseModal = useCallback(() => {
        setVideoDurationMs(null)
        setIsModalOpen(false)
    }, [])

    const handleOpenModal = useCallback(() => {
        setVideoDurationMs(null)
        setIsModalOpen(true)
    }, [])

    const handleVideoDurationChange = useCallback((duration: number) => {
        setVideoDurationMs(duration)
    }, [])

    const handlePreviewMediaAdvance = useCallback(() => {
        setVideoDurationMs(null)
        setCurrentIndex((prev) => {
            if (mediaItems.length === 0) return prev
            if (prev < mediaItems.length - 1) return prev + 1
            return 0
        })

        if (mediaItems.length <= 1) {
            setReplayNonce((n) => n + 1)
        }
    }, [mediaItems.length])

    const handleModalMediaAdvance = useCallback(() => {
        const isLastItem = currentIndex >= mediaItems.length - 1
        if (isLastItem) {
            setVideoDurationMs(null)
            setIsModalOpen(false)
            return
        }
        setCurrentIndex((prev) => prev + 1)
    }, [currentIndex, mediaItems.length])

    const handleModalMediaPrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev))
    }, [])

    useEffect(() => {
        if (mediaItems.length === 0) return

        const nextIndex = (currentIndex + 1) % mediaItems.length
        const nextItem = mediaItems[nextIndex]

        if (nextItem?.type === 'image') {
            const img = new window.Image()
            img.src = nextItem.src
        }
    }, [currentIndex, mediaItems])

    const currentDurationMs = useMemo(() => {
        const item = mediaItems[currentIndex]
        if (!item) return MEDIA_FLOATER_DEFAULT_IMAGE_MS
        if (item.type === 'image') {
            return item.duration ?? MEDIA_FLOATER_DEFAULT_IMAGE_MS
        }
        return videoDurationMs ?? MEDIA_FLOATER_DEFAULT_VIDEO_MS
    }, [mediaItems, currentIndex, videoDurationMs])

    // Suppress the promo floater during an active Smiles session — verification can resolve
    // after mount, so gate the render too, not only the show-once effect above.
    if (!isVisible || mediaItems.length === 0 || isSmilesSession) {
        return null
    }

    return (
        <>
            {!isModalOpen && (
                <MediaFloater
                    mediaItems={mediaItems}
                    currentIndex={currentIndex}
                    currentDurationMs={currentDurationMs}
                    onOpen={handleOpenModal}
                    onClose={handleClose}
                    onMediaAdvance={handlePreviewMediaAdvance}
                    onVideoDurationChange={handleVideoDurationChange}
                    exploreOffersUrl={exploreOffersUrl}
                    replayNonce={replayNonce}
                />
            )}

            {isModalOpen && (
                <MediaFloaterModal
                    mediaItems={mediaItems}
                    currentIndex={currentIndex}
                    currentDurationMs={currentDurationMs}
                    onClose={handleCloseModal}
                    onMediaAdvance={handleModalMediaAdvance}
                    onMediaPrevious={handleModalMediaPrevious}
                    onVideoDurationChange={handleVideoDurationChange}
                    exploreOffersUrl={exploreOffersUrl}
                />
            )}
        </>
    )
}

export { MediaFloaterWrapper }
export default MediaFloaterWrapper
export type { MediaFloaterProps, MediaItem }
